<template>
  <span v-if="state" class="agent-update" :class="`_${state}`" v-tooltip="{ content: hint, triggers: ['hover', 'click'] }">
    <SatIcon :name="icon" :size="12" />
    {{ version }}
  </span>
</template>
<script>
import SatIcon from "@/components/Satellites/SatIcon.vue";

// Next to the calypso version: a newer release is available, the requested update is running or has failed.
export default {
  name: "AgentUpdateBadge",
  components: { SatIcon },
  props: {
    // agent_update of the satellite
    update: {
      type: Object,
      default: null,
    },
  },
  computed: {
    state() {
      const update = this.update;
      if (!update) return null;
      if (update.status === "pending" || update.status === "failed") return update.status;
      if (update.available) return update.remote ? "available" : "manual";
      return null;
    },
    // The version the badge is about: the one being installed, or the latest
    version() {
      return this.state === "pending" || this.state === "failed" ? this.update.target_version : this.update.latest_version;
    },
    icon() {
      return { pending: "refresh", failed: "alert" }[this.state] || "arrow-up-circle";
    },
    hint() {
      const update = this.update;
      switch (this.state) {
        case "pending":
          return this.$t("satellites.update.pending", { version: update.target_version });
        case "failed":
          // The error comes from the updater on the server, as a sentence without the final period.
          return [
            this.$t("satellites.update.failed", { version: update.target_version }),
            update.error && `${update.error.replace(/[.\s]+$/, "")}.`,
            this.$t("satellites.update.failed_hint"),
          ]
            .filter(Boolean)
            .join(" ");
        case "manual":
          return `${this.$t("satellites.update.available", { version: update.latest_version })} ${this.$t("satellites.update.manual")}`;
        default:
          return this.$t("satellites.update.available", { version: update.latest_version });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.agent-update {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  vertical-align: middle;
  cursor: default;
  color: var(--sat-accent);
  background: var(--sat-accent-soft);

  &._manual {
    color: var(--sat-text-2);
    background: var(--sat-hover);
  }

  &._pending {
    color: var(--sat-warn);
    background: var(--sat-warn-soft);

    .sat-icon {
      animation: agent-update-spin 1.2s linear infinite;
    }
  }

  &._failed {
    color: var(--sat-danger);
    background: var(--sat-danger-soft);
  }
}

@keyframes agent-update-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
